import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiPlus, FiRefreshCw, FiDatabase, FiChevronDown, FiChevronUp, FiTrash2, FiSearch, FiX, FiEdit2, FiCloud, FiHardDrive } from 'react-icons/fi';
import { ALL_TABLES, TableDef, ColumnDef } from '../data/tables';
import { fetchTableData, updateRow, insertRow, deleteRow, isApiAvailable } from '../data/dbViewerService';
import { confirmDiscardChanges, setFormDirty } from '../../../lib/unsavedChanges';
import './DbViewer.css';

// =============================================
// Inline Editable Cell
// =============================================
interface EditableCellProps {
  value: any;
  column: ColumnDef;
  onSave: (newValue: any) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({ value, column, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const startEdit = () => {
    if (!column.editable) return;
    setEditValue(value === null || value === undefined ? '' : String(value));
    setEditing(true);
  };

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const save = () => {
    setEditing(false);
    let newVal: any = editValue.trim();

    // Convert types
    if (newVal === '') {
      newVal = column.notNull ? value : null; // revert if required, null if optional
    } else if (column.type === 'smallint' || column.type === 'integer') {
      newVal = parseInt(newVal) || 0;
    }

    if (newVal !== value) {
      onSave(newVal);
    }
  };

  const cancel = () => {
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && column.type !== 'text') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      cancel();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      save();
      const td = (e.target as HTMLElement).closest('td');
      if (!td) return;
      const tr = td.closest('tr');
      if (!tr) return;
      const cells = Array.from(tr.querySelectorAll('td.db-td-editable'));
      const idx = cells.indexOf(td);
      const next = e.shiftKey ? cells[idx - 1] : cells[idx + 1];
      if (next) (next as HTMLElement).click();
    }
  };

  // Editing mode
  if (editing) {
    const inputProps = {
      value: editValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEditValue(e.target.value),
      onBlur: save,
      onKeyDown: handleKeyDown,
      className: 'db-cell-input',
      maxLength: column.maxLength,
    };

    if (column.type === 'text') {
      return (
        <td className="db-td-editing">
          <textarea ref={inputRef as React.RefObject<HTMLTextAreaElement>} {...inputProps} rows={2} />
        </td>
      );
    }

    return (
      <td className="db-td-editing">
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={column.type === 'smallint' || column.type === 'integer' ? 'number' : 'text'}
          {...inputProps}
        />
      </td>
    );
  }

  // Display mode
  const displayContent = value === null || value === undefined ? (
    <span className="db-null">NULL</span>
  ) : column.type === 'smallint' ? (
    <span className={`db-badge ${value === 1 ? 'db-badge-active' : 'db-badge-inactive'}`}>
      {value}
    </span>
  ) : (
    <span className="db-cell-text">{String(value)}</span>
  );

  return (
    <td
      className={`${column.primaryKey ? 'db-td-pk' : ''} ${column.editable ? 'db-td-editable' : 'db-td-readonly'}`}
      onClick={startEdit}
      title={column.editable ? 'Click to edit' : 'Read-only'}
    >
      {displayContent}
      {column.editable && <FiEdit2 className="db-edit-hint" size={10} />}
    </td>
  );
};

// =============================================
// Main DbViewer
// =============================================
interface DbViewerProps {
  // Embed scoped to a single table (e.g. inside Roles & Permissions tabs)
  initialTable?: string;
  lockTable?: boolean;  // hide the table selector — caller controls the table
}

const DbViewer: React.FC<DbViewerProps> = ({ initialTable, lockTable = false }) => {
  const [selectedTable, setSelectedTable] = useState<string>(() => {
    if (initialTable && ALL_TABLES.some(t => t.name === initialTable)) return initialTable;
    const saved = localStorage.getItem('akt_db_viewer_last_table');
    if (saved && ALL_TABLES.some(t => t.name === saved)) return saved;
    return ALL_TABLES[0]?.name || '';
  });
  const [tableData, setTableData] = useState<Record<string, any>[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRow, setNewRow] = useState<Record<string, any>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [tableFilter, setTableFilter] = useState('');
  const [isTableDropdownOpen, setIsTableDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Guard-then-land: changing the viewed table must check for unsaved work first.
  const selectTable = async (name: string) => {
    const closeDropdown = () => { setTableFilter(''); setIsTableDropdownOpen(false); setHighlightedIndex(-1); };
    if (name === selectedTable) { closeDropdown(); return; }
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    setSelectedTable(name);
    closeDropdown();
  };
  const [useApi, setUseApi] = useState(false);
  const [apiChecked, setApiChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const tableDropdownRef = useRef<HTMLDivElement>(null);
  const tableFilterInputRef = useRef<HTMLInputElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    isApiAvailable().then(available => {
      setUseApi(available);
      setApiChecked(true);
    }).catch(() => {
      setApiChecked(true);
    });
  }, []);

  useEffect(() => {
    // Don't clobber the standalone viewer's last-table when embedded/locked
    if (selectedTable && !lockTable) localStorage.setItem('akt_db_viewer_last_table', selectedTable);
  }, [selectedTable, lockTable]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tableDropdownRef.current && !tableDropdownRef.current.contains(e.target as Node)) {
        setIsTableDropdownOpen(false);
        setTableFilter('');
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTables = ALL_TABLES.filter(t => {
    if (!tableFilter) return true;
    const q = tableFilter.toLowerCase();
    return t.displayName.toLowerCase().includes(q) || t.name.toLowerCase().includes(q);
  });

  const currentTable: TableDef | undefined = ALL_TABLES.find(t => t.name === selectedTable);

  // Load table data
  const loadData = useCallback(async () => {
    if (!selectedTable) return;
    setLoading(true);
    try {
      const rows = await fetchTableData(selectedTable);
      setTableData(rows);
    } catch (err: any) {
      // Database is the single source of truth — surface the error, never fall back to local data.
      setTableData([]);
      setNotification({ message: err.message || `Failed to load "${selectedTable}" from the database`, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedTable, useApi]);

  useEffect(() => {
    loadData();
    setShowAddForm(false);
    setNewRow({});
    setSearchTerm('');
    setSortColumn('');
    setSelectedRows(new Set());
  }, [selectedTable, loadData]);

  // Show notification
  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Add new row
  const handleAddRow = async () => {
    if (!currentTable) return;

    const missingFields = currentTable.columns
      .filter(col => col.notNull && col.editable && !newRow[col.name]?.toString().trim())
      .map(col => col.name);

    if (missingFields.length > 0) {
      showNotification(`Required fields missing: ${missingFields.join(', ')}`, 'error');
      return;
    }

    try {
      const row: Record<string, any> = {};
      currentTable.columns.forEach(col => {
        if (col.primaryKey) return; // let DB handle serial
        if (col.type === 'timestamp') return; // let DB handle defaults
        if (col.name in newRow) {
          if (col.type === 'smallint' || col.type === 'integer') {
            row[col.name] = parseInt(newRow[col.name]) || 0;
          } else {
            row[col.name] = newRow[col.name];
          }
        } else if (col.default && col.default !== 'NOW') {
          row[col.name] = col.default;
        }
      });
      const inserted = await insertRow(selectedTable, row);
      const pkCol = currentTable.columns.find(c => c.primaryKey);
      const newId = pkCol ? inserted[pkCol.name] : '?';
      setNewRow({});
      setShowAddForm(false);
      showNotification(`Row added to ${currentTable.displayName} (ID: ${newId})`);
      loadData();
    } catch (err: any) {
      showNotification(err.message || 'Insert failed', 'error');
    }
  };

  // Inline edit — save a single cell
  const handleCellSave = async (rowIndex: number, colName: string, newValue: any) => {
    const pkCol = currentTable?.columns.find(c => c.primaryKey);
    if (!pkCol) {
      showNotification('Cannot edit: table has no primary key', 'error');
      return;
    }
    const rowId = tableData[rowIndex][pkCol.name];
    try {
      await updateRow(selectedTable, rowId, { [colName]: newValue });
      showNotification(`Row ${rowId}: ${colName} updated`);
      loadData();
    } catch (err: any) {
      showNotification(err.message || 'Update failed', 'error');
    }
  };

  // Delete single row
  const handleDeleteRow = async (index: number) => {
    const pkCol = currentTable?.columns.find(c => c.primaryKey);
    if (!pkCol) {
      showNotification('Cannot delete: table has no primary key', 'error');
      return;
    }
    const rowId = tableData[index][pkCol.name];
    try {
      await deleteRow(selectedTable, rowId);
      showNotification(`Row ${rowId} deleted`);
      setSelectedRows(prev => { const next = new Set(prev); next.delete(index); return next; });
      loadData();
    } catch (err: any) {
      showNotification(err.message || 'Delete failed', 'error');
    }
  };

  // Delete selected rows
  const handleDeleteSelected = async () => {
    if (selectedRows.size === 0) return;
    const indices = Array.from(selectedRows).sort((a, b) => b - a);
    const pkCol = currentTable?.columns.find(c => c.primaryKey);
    if (!pkCol) {
      showNotification('Cannot delete: table has no primary key', 'error');
      return;
    }
    const ids = indices.map(i => tableData[i]?.[pkCol.name]).filter(Boolean);
    try {
      for (const id of ids) {
        await deleteRow(selectedTable, id);
      }
      setSelectedRows(new Set());
      showNotification(`Deleted ${ids.length} row(s): IDs ${ids.join(', ')}`);
      loadData();
    } catch (err: any) {
      showNotification(err.message || 'Delete failed', 'error');
      loadData();
    }
  };

  // Row selection
  const toggleRowSelection = (index: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === displayData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(displayData.map((_, i) => i)));
    }
  };

  // Reload data from current source
  const handleReload = () => {
    setSelectedRows(new Set());
    loadData();
    showNotification(`${currentTable?.displayName} reloaded`);
  };

  // Sort
  const handleSort = (colName: string) => {
    if (sortColumn === colName) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(colName);
      setSortDirection('asc');
    }
  };

  // Filter + sort data
  const displayData = React.useMemo(() => {
    let filtered = tableData;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = tableData.filter(row =>
        Object.values(row).some(val =>
          val?.toString().toLowerCase().includes(term)
        )
      );
    }

    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        const cmp = typeof aVal === 'number'
          ? aVal - bVal
          : aVal.toString().localeCompare(bVal.toString());
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    }

    return filtered;
  }, [tableData, searchTerm, sortColumn, sortDirection]);

  // Map display index back to tableData index for editing
  const getOriginalIndex = (displayRow: Record<string, any>): number => {
    const pkCol = currentTable?.columns.find(c => c.primaryKey);
    if (pkCol) {
      return tableData.findIndex(r => r[pkCol.name] === displayRow[pkCol.name]);
    }
    return tableData.indexOf(displayRow);
  };

  if (!currentTable) return null;

  return (
    <div className="db-viewer">
      {/* Notification */}
      {notification && (
        <div className={`db-notification db-notification-${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Table Selector */}
      {!lockTable && (
      <div className="db-header">
        <div className="db-table-selector" ref={tableDropdownRef}>
          <FiDatabase className="db-icon" />
          <div className="db-table-dropdown">
            <div
              className={`db-table-dropdown-input ${isTableDropdownOpen ? 'open' : ''}`}
              onClick={() => {
                const opening = !isTableDropdownOpen;
                setIsTableDropdownOpen(opening);
                if (opening) {
                  setTimeout(() => {
                    tableFilterInputRef.current?.focus();
                    // Center the selected table within the dropdown list. The browser
                    // clamps scrollTop, so items near the top/bottom naturally show at
                    // the top/bottom instead of being forced to the middle.
                    const list = dropdownListRef.current;
                    const sel = list?.querySelector('.selected') as HTMLElement | null;
                    if (list && sel) {
                      list.scrollTop = sel.offsetTop - (list.clientHeight - sel.clientHeight) / 2;
                    }
                  }, 0);
                } else {
                  setTableFilter('');
                  setHighlightedIndex(-1);
                }
              }}
            >
              <input
                ref={tableFilterInputRef}
                type="text"
                placeholder={selectedTable ? `${currentTable?.displayName} (${currentTable?.name})` : 'Select a table...'}
                value={tableFilter}
                onChange={e => {
                  setTableFilter(e.target.value);
                  setHighlightedIndex(0);
                  setIsTableDropdownOpen(true);
                }}
                onFocus={() => setIsTableDropdownOpen(true)}
                onKeyDown={e => {
                  if (!isTableDropdownOpen) return;
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setHighlightedIndex(prev => Math.min(prev + 1, filteredTables.length - 1));
                    setTimeout(() => dropdownListRef.current?.querySelector('.highlighted')?.scrollIntoView({ block: 'nearest' }), 0);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setHighlightedIndex(prev => Math.max(prev - 1, 0));
                    setTimeout(() => dropdownListRef.current?.querySelector('.highlighted')?.scrollIntoView({ block: 'nearest' }), 0);
                  } else if (e.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < filteredTables.length) {
                    e.preventDefault();
                    selectTable(filteredTables[highlightedIndex].name);
                  } else if (e.key === 'Escape') {
                    setIsTableDropdownOpen(false);
                    setTableFilter('');
                    setHighlightedIndex(-1);
                  }
                }}
              />
              {tableFilter && (
                <FiX
                  className="db-table-clear"
                  onClick={e => { e.stopPropagation(); setTableFilter(''); tableFilterInputRef.current?.focus(); }}
                />
              )}
              <FiChevronDown className={`db-table-chevron ${isTableDropdownOpen ? 'rotated' : ''}`} />
            </div>
            {isTableDropdownOpen && (
              <div className="db-table-dropdown-list" ref={dropdownListRef}>
                {filteredTables.length === 0 ? (
                  <div className="db-table-dropdown-empty">No tables match</div>
                ) : (
                  filteredTables.map((table, idx) => (
                    <div
                      key={table.name}
                      className={`db-table-dropdown-item ${table.name === selectedTable ? 'selected' : ''} ${idx === highlightedIndex ? 'highlighted' : ''}`}
                      onClick={() => selectTable(table.name)}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                    >
                      {table.displayName} ({table.name})
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        <p className="db-table-desc">{currentTable?.description}</p>
      </div>
      )}

      {/* Toolbar */}
      <div className="db-toolbar">
        <div className="db-toolbar-search">
          <FiSearch className="db-search-icon" />
          <input
            type="text"
            placeholder="Search all columns..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="db-search-clear" onClick={() => setSearchTerm('')}>
              <FiX />
            </button>
          )}
        </div>
        <div className="db-toolbar-actions">
          {apiChecked && (
            <span
              className={`db-source-badge ${useApi ? 'db-source-pg' : 'db-source-local'}`}
              title={useApi ? 'Connected to the database — all reads/writes go to the DB' : 'Database API not reachable — operations will error (no local fallback)'}
            >
              {useApi ? <><FiCloud /> Database</> : <><FiHardDrive /> DB offline</>}
            </span>
          )}
          <span className="db-row-count">
            {displayData.length} of {tableData.length} row{tableData.length !== 1 ? 's' : ''}
          </span>
          {selectedRows.size > 0 && (
            <button
              className="db-btn db-btn-delete-selected"
              onClick={handleDeleteSelected}
            >
              <FiTrash2 /> Delete {selectedRows.size} Selected
            </button>
          )}
          <button
            className="db-btn db-btn-add"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <FiPlus /> Add Row
          </button>
          <button className="db-btn db-btn-reset" onClick={handleReload}>
            <FiRefreshCw /> Reload
          </button>
        </div>
      </div>

      {/* Add Row Form */}
      {showAddForm && (
        <div className="db-add-form">
          <h3>Add New Row</h3>
          <div className="db-add-fields">
            {currentTable.columns
              .filter(col => col.editable)
              .map(col => (
                <div key={col.name} className="db-add-field">
                  <label>
                    {col.name}
                    {col.notNull && <span className="db-required">*</span>}
                    <span className="db-field-type">{col.type}{col.maxLength ? `(${col.maxLength})` : ''}</span>
                  </label>
                  {col.type === 'text' ? (
                    <textarea
                      value={newRow[col.name] || ''}
                      onChange={e => setNewRow({ ...newRow, [col.name]: e.target.value })}
                      placeholder={col.name}
                      rows={2}
                    />
                  ) : (
                    <input
                      type={col.type === 'smallint' || col.type === 'integer' ? 'number' : 'text'}
                      value={newRow[col.name] ?? (col.default && col.default !== 'NOW' ? col.default : '')}
                      onChange={e => setNewRow({ ...newRow, [col.name]: e.target.value })}
                      placeholder={col.default && col.default !== 'NOW' ? `Default: ${col.default}` : col.name}
                      maxLength={col.maxLength}
                    />
                  )}
                </div>
              ))}
          </div>
          <div className="db-add-actions">
            <button className="db-btn db-btn-save" onClick={handleAddRow}>
              <FiPlus /> Insert Row
            </button>
            <button className="db-btn db-btn-cancel" onClick={() => { setShowAddForm(false); setNewRow({}); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Editing hint */}
      <div className="db-edit-legend">
        <span className="db-legend-item"><FiEdit2 size={11} /> Click any highlighted cell to edit inline</span>
        <span className="db-legend-item"><span className="db-legend-key">Enter</span> save <span className="db-legend-key">Esc</span> cancel <span className="db-legend-key">Tab</span> save &amp; next</span>
      </div>

      {/* Data Table */}
      {loading && <div className="db-loading">Loading from PostgreSQL...</div>}
      <div className="db-table-wrapper">
        <table className="db-table">
          <thead>
            <tr>
              <th className="db-th-checkbox">
                <input
                  type="checkbox"
                  checked={displayData.length > 0 && selectedRows.size === displayData.length}
                  onChange={toggleSelectAll}
                  title="Select all"
                />
              </th>
              <th className="db-th-actions">#</th>
              {currentTable.columns.map(col => (
                <th
                  key={col.name}
                  className={`db-th-sortable ${sortColumn === col.name ? 'db-th-sorted' : ''}`}
                  onClick={() => handleSort(col.name)}
                >
                  <div className="db-th-content">
                    <span>{col.name}</span>
                    {sortColumn === col.name && (
                      sortDirection === 'asc' ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />
                    )}
                  </div>
                  <span className="db-th-type">
                    {col.type}{col.primaryKey ? ' PK' : ''}{col.editable ? '' : ' 🔒'}
                  </span>
                </th>
              ))}
              <th className="db-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayData.length === 0 ? (
              <tr>
                <td colSpan={currentTable.columns.length + 3} className="db-empty">
                  {searchTerm ? 'No matching rows found' : 'No data. Click "Add Row" to insert.'}
                </td>
              </tr>
            ) : (
              displayData.map((row, displayIdx) => {
                const originalIdx = getOriginalIndex(row);
                return (
                  <tr key={displayIdx} className={selectedRows.has(displayIdx) ? 'db-row-selected' : ''}>
                    <td className="db-td-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedRows.has(displayIdx)}
                        onChange={() => toggleRowSelection(displayIdx)}
                      />
                    </td>
                    <td className="db-td-rownum">{displayIdx + 1}</td>
                    {currentTable.columns.map(col => {
                      if (selectedTable === 'site_table_registry' && col.name === 'table_name' && row[col.name]) {
                        const tableName = String(row[col.name]);
                        const tableExists = ALL_TABLES.some(t => t.name === tableName);
                        return (
                          <td key={col.name} className="db-td-readonly">
                            {tableExists ? (
                              <span
                                className="db-cell-link"
                                onClick={() => setSelectedTable(tableName)}
                                title={`Open ${tableName}`}
                              >
                                {tableName}
                              </span>
                            ) : (
                              <span className="db-cell-text">{tableName}</span>
                            )}
                          </td>
                        );
                      }
                      return (
                        <EditableCell
                          key={col.name}
                          value={row[col.name]}
                          column={col}
                          onSave={(newValue) => handleCellSave(originalIdx, col.name, newValue)}
                        />
                      );
                    })}
                    <td className="db-td-actions">
                      <button
                        className="db-btn-icon db-btn-delete"
                        onClick={() => handleDeleteRow(originalIdx)}
                        title="Delete row"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Schema Info */}
      <div className="db-schema-info">
        <h4>Schema: {currentTable.name}</h4>
        <div className="db-schema-cols">
          {currentTable.columns.map(col => (
            <span key={col.name} className={`db-schema-col ${col.editable ? '' : 'db-schema-col-readonly'}`}>
              {col.primaryKey && <span className="db-pk-badge">PK</span>}
              <strong>{col.name}</strong> {col.type}
              {col.maxLength ? `(${col.maxLength})` : ''}
              {col.notNull ? ' NOT NULL' : ''}
              {col.default ? ` DEFAULT ${col.default}` : ''}
              {!col.editable && ' 🔒'}
            </span>
          ))}
        </div>
        <p className="db-schema-note">
          {useApi
            ? 'Connected to the database. Click any editable cell to modify — changes save directly to the DB.'
            : 'Database API not reachable. Reads and writes will error until it is available — there is no local fallback.'}
        </p>
      </div>
    </div>
  );
};

export default DbViewer;
